"use client";

import { useContext } from "react";
import type { CalendarEvent } from "@wtc/ui";

import { CalendarContext } from "./context";
import type { CalendarContextType, CalendarSource } from "./types";

export function useCalendarContext(): CalendarContextType {
  const context = useContext(CalendarContext);
  if (context === undefined) {
    throw new Error(
      "useCalendarContext must be used within a CalendarProvider",
    );
  }

  return context;
}

/**
 * Non-throwing calendar context accessor for optional integrations.
 * Returns undefined when CalendarProvider is not mounted.
 */
export function useOptionalCalendarContext(): CalendarContextType | undefined {
  return useContext(CalendarContext);
}

// Convenience hook for getting events in a specific date range
export function useCalendarEvents(
  startDate?: Date,
  endDate?: Date,
): CalendarEvent[] {
  const { events } = useCalendarContext();

  if (!startDate || !endDate) {
    return events;
  }

  return events.filter((event) => {
    const eventStart = new Date(event.start);

    return eventStart >= startDate && eventStart <= endDate;
  });
}

// Convenience hook for getting a specific event by ID
export function useCalendarEvent(eventId: string): CalendarEvent | undefined {
  const { events } = useCalendarContext();

  return events.find((event) => event.id === eventId);
}

// Convenience hook for getting calendar sources
export function useCalendarSources(): CalendarSource[] {
  const { calendarSources } = useCalendarContext();

  return calendarSources;
}

// Convenience hook for getting source colors
export function useSourceColors(): Record<string, string> {
  const { sourceColors } = useCalendarContext();

  return sourceColors;
}

// Convenience hook for getting color for a specific source
export function useSourceColor(sourceEmail: string): string | undefined {
  const { sourceColors } = useCalendarContext();

  return sourceColors[sourceEmail];
}
